/** File helpers shared by the upload wizard and CSV importers. */

const EXTENSION_MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  csv: 'text/csv',
};

/**
 * Resolves the backend-accepted MIME type for a file, falling back to the
 * extension when the browser reports an empty or generic type.
 */
export function resolveDocumentMimeType(file: File): string {
  if (Object.values(EXTENSION_MIME_TYPES).includes(file.type)) {
    return file.type;
  }

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_MIME_TYPES[extension] ?? 'application/pdf';
}

/** "Q4 Report.pdf" → "Q4 Report". */
export function fileNameStem(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

/** Reads a file as UTF-8 text (CSV uploads travel as inline JSON strings). */
export function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsText(file);
  });
}

/** Backend CSV validation errors: `details.rowErrors` from a 400 response. */
export interface CsvRowError {
  line: number;
  issues: { path: string; message: string }[];
}

/** Narrows unknown error `details` into the CSV row-error list, if present. */
export function extractRowErrors(details: Record<string, unknown> | undefined): CsvRowError[] {
  const raw = details?.rowErrors;

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.filter(
    (entry): entry is CsvRowError =>
      typeof entry === 'object' && entry !== null && 'line' in entry && 'issues' in entry,
  );
}
